import React from 'react';
import ChangeIndicatorDI from '@atlaskit/icon/glyph/hipchat/media-attachment-count';
import { Section as SectionDI, SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { TitleHeader as TitleHeaderDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import type { LanguageKey } from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTranslationStoreState } from '../../controllers/manage-translations/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const makePanelUrl = ({
	projectKey,
	languageKey,
	panelId,
}: {
	projectKey: ProjectKey;
	languageKey: LanguageKey;
	panelId: string;
}) => `/jira/servicedesk/projects/${projectKey}/settings/language/${languageKey}/${panelId}`;

export const SidebarTranslations = ({
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons -- https://jbusiness.atlassian.net/browse/FUN-1796
	ChangeIndicator = ChangeIndicatorDI,
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	TitleHeader = TitleHeaderDI,
}: Props) => {
	const { translationTabsWithPanelIds, selectedLanguage, currentLanguage, translationPanelMap } =
		useTranslationStoreState();
	const { data: project } = useProject();
	const { formatMessage } = useIntl();

	if (currentLanguage == null) {
		return <SkeletonItem />;
	}

	if (!project || !translationPanelMap) {
		return null;
	}

	return (
		<>
			<TitleHeader>
				{formatMessage(messages.title, { language: selectedLanguage?.label })}
			</TitleHeader>
			{translationTabsWithPanelIds
				.filter((tTab) => tTab.translationPanelIds.length > 0)
				.map((tab) => (
					<Section key={tab.name} title={tab.name}>
						{tab.translationPanelIds.map((panelId) => {
							const panel = translationPanelMap[panelId];
							const { id, isFlagged } = panel;
							const panelUrl = makePanelUrl({
								projectKey: project.key,
								languageKey: currentLanguage,
								panelId: id,
							});
							return (
								<MenuItem
									analytics={{ itemId: id }}
									iconAfter={
										isFlagged ? (
											<ChangeIndicator
												primaryColor={token('color.icon.warning')}
												label={formatMessage(messages.changeIndicatorLabel)}
											/>
										) : null
									}
									key={id}
									selectedOn={matchUrlPathname()}
									href={panelUrl}
								>
									{panel.displayName}
								</MenuItem>
							);
						})}
					</Section>
				))}
		</>
	);
};
